<template>
  <div class="products-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-image">
        <img src="@/assets/images/ambience/data.jpg" alt="Hero background" />
      </div>
      <div class="hero-content">
        <h1>{{ $t('productsPage.hero.title') }}</h1>
        <h2>{{ $t('productsPage.hero.subtitle') }}</h2>
        <p class="description">
          {{ $t('productsPage.hero.description') }}
        </p>
      </div>
    </section>

    <!-- Forecast Section -->
    <section id="forecast" class="product-section">
      <h2>{{ $t('productsPage.forecast.title') }}</h2>
      <div class="forecast-features-stepper">
        <div class="forecast-features-steps">
          <div
            v-for="(feature, idx) in forecastFeatures"
            :key="idx"
            :id="`forecast-feature-${idx}`"
            class="forecast-feature-step"
          >
            <h3>{{ feature.title }}</h3>
            <p class="feature-text">{{ feature.text }}</p>
          </div>
        </div>
        <div class="product-image-container">
          <ScrollingImageInFrame 
            :frame-image-src="laptopImage"
            :content-image-src="featureImages[activeFeatureIdx]"
            :content-area-ratio="{ top: 0.287, left: 0.155, width: 0.7, height: 0.5 }"
            :scroll-trigger-offset-bottom="200"
            :scroll-anchors="featureAnchorIds"
            :anchor-progresses="[0, 0, 0.3, 0.7]"
          />
        </div>
      </div>
    </section>


    <!-- Kundeanmeldelser Section -->
    <section id="kundeanmeldelser" class="product-section">
      <h2>{{ $t('productsPage.reviews.title') }}</h2>
      <div class="forecast-features-stepper">
        <div class="product-image-container">
          <ScrollingImageInFrame 
            :frame-image-src="laptopImage"
            :content-image-src="reviewFeatureImages[activeReviewFeatureIdx]"
            :content-area-ratio="{ top: 0.287, left: 0.155, width: 0.7, height: 0.5 }"
            :scroll-trigger-offset-bottom="200"
            :scroll-anchors="reviewFeatureAnchorIds"
            :anchor-progresses="[0, 0, 0.5, 1]"
          />
        </div>
        <div class="forecast-features-steps">
          <div
            v-for="(feature, idx) in reviewFeatures"
            :key="idx"
            :id="`review-feature-${idx}`"
            class="forecast-feature-step"
          >
            <h3>{{ feature.title }}</h3>
            <p class="feature-text">{{ feature.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Rapporter Section -->
    <section id="rapporter" class="product-section">
      <h2>{{ $t('productsPage.reports.title') }}</h2>
      <div class="product-content">
        <div class="product-description">
          <p class="lead-text">
            {{ $t('productsPage.reports.leadText') }}
          </p>

          <div class="features">
            <h3>{{ $t('productsPage.reports.featuresTitle') }}</h3>
            <ul>
              <li>
                <strong>{{ $t('productsPage.reports.feature1Title') }}</strong>
                <p class="feature-text">{{ $t('productsPage.reports.feature1Text') }}</p>
              </li>
              <li>
                <strong>{{ $t('productsPage.reports.feature2Title') }}</strong>
                <p class="feature-text">{{ $t('productsPage.reports.feature2Text') }}</p>
              </li>
              <li>
                <strong>{{ $t('productsPage.reports.feature3Title') }}</strong>
                <p class="feature-text">{{ $t('productsPage.reports.feature3Text') }}</p>
              </li>
              <li>
                <strong>{{ $t('productsPage.reports.feature4Title') }}</strong>
                <p class="feature-text">{{ $t('productsPage.reports.feature4Text') }}</p>
              </li>
            </ul>
          </div>

          <div class="use-cases">
            <h3>{{ $t('productsPage.reports.useCasesTitle') }}</h3>
            <p class="feature-text">
             {{ $t('productsPage.reports.useCasesText') }}
            </p>
          </div>
          <div class="feedback-section">
            <h4>{{ $t('productsPage.reports.feedbackTitle') }}</h4>
            <p class="feature-text">
              {{ $t('productsPage.reports.feedbackText') }}
            </p>
            <div class="feedback-cta">
              <TextWithArrowButton 
                :text="$t('productsPage.reports.feedbackButton')" 
                @click="$router.push({ path: '/kontakt', query: { subject: 'Feedback om rapporter og analyser' }})"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <h2>{{ $t('productsPage.finalCta.title') }}</h2>
      <p class="feature-text">
        {{ $t('productsPage.finalCta.text') }}
      </p>
      <div class="hero-cta">
        <TextWithArrowButton :text="$t('productsPage.finalCta.button')" @click="$router.push('/kontakt', { query: { subject: 'Book møde/opkald' }})" />
      </div>
    </section>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { ref, onMounted, onUnmounted } from 'vue';
import TextWithArrowButton from '@/public/components/ui/TextWithArrowButton.vue'
import ScrollingImageInFrame from '@/public/components/ui/ScrollingImageInFrame.vue'

// Import images
import laptopImage from '@/assets/images/products/laptop_2.png';
import salesForecastImage from '@/assets/images/products/salesforecast-picture.png';
import salesForecastOverview from '@/assets/images/products/salesforecast-overview.png';
import npsOverviewGuest from '@/assets/images/products/overview-nps-guest.png';
import npsReport from '@/assets/images/products/nps-report.png';

export default {
  name: 'ProductPage',
  components: {
    TextWithArrowButton,
    ScrollingImageInFrame
  },
  setup() {
    const { t } = useI18n()
    const forecastFeatures = [
      {
        title: t('productsPage.forecast.feature1Title'),
        text: t('productsPage.forecast.feature1Text')
      },
      {
        title: t('productsPage.forecast.feature2Title'),
        text: t('productsPage.forecast.feature2Text')
      },
      {
        title: t('productsPage.forecast.feature3Title'),
        text: t('productsPage.forecast.feature3Text')
      },
      {
        title: t('productsPage.forecast.feature4Title'),
        text: t('productsPage.forecast.feature4Text')
      }
    ];
    const featureAnchorIds = forecastFeatures.map((_, idx) => `forecast-feature-${idx}`);
    const featureImages = [
      salesForecastOverview,
      salesForecastImage,
      salesForecastImage,
      salesForecastImage
    ];
    const activeFeatureIdx = ref(0);
    const updateActiveFeature = () => {
      let minDist = Infinity;
      let idx = 0;
      featureAnchorIds.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          const dist = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
          if (dist < minDist) {
            minDist = dist;
            idx = i;
          }
        }
      });
      activeFeatureIdx.value = idx;
    };
    onMounted(() => {
      window.addEventListener('scroll', updateActiveFeature);
    });
    onUnmounted(() => {
      window.removeEventListener('scroll', updateActiveFeature);
    });
    const reviewFeatures = [
      {
        title: t('productsPage.reviews.feature1Title'),
        text: t('productsPage.reviews.feature1Text')
      },
      {
        title: t('productsPage.reviews.feature2Title'),
        text: t('productsPage.reviews.feature2Text')
      },
      {
        title: t('productsPage.reviews.feature3Title'),
        text: t('productsPage.reviews.feature3Text')
      },
      {
        title: t('productsPage.reviews.feature4Title'),
        text: t('productsPage.reviews.feature4Text')
      }
    ];
    const reviewFeatureAnchorIds = reviewFeatures.map((_, idx) => `review-feature-${idx}`);
    const reviewFeatureImages = [
      npsOverviewGuest,
      npsOverviewGuest,
      npsReport,
      npsReport
    ];
    const activeReviewFeatureIdx = ref(0);
    const updateActiveReviewFeature = () => {
      let minDist = Infinity;
      let idx = 0;
      reviewFeatureAnchorIds.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          const dist = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
          if (dist < minDist) {
            minDist = dist;
            idx = i;
          }
        }
      });
      activeReviewFeatureIdx.value = idx;
    };
    onMounted(() => {
      window.addEventListener('scroll', updateActiveReviewFeature);
    });
    onUnmounted(() => {
      window.removeEventListener('scroll', updateActiveReviewFeature);
    });
    return { 
      t,
      laptopImage,
      salesForecastImage,
      forecastFeatures,
      featureAnchorIds,
      featureImages,
      activeFeatureIdx,
      reviewFeatures,
      reviewFeatureAnchorIds,
      reviewFeatureImages,
      activeReviewFeatureIdx
    }
  }
}
</script>

<style scoped>
.hero {
  position: relative;
  padding: 12rem 2rem;
  width: 100%;
  margin-left: -1.5rem;
  margin-top: -1.7rem;
  text-align: center;
  overflow: hidden;
}

.hero-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.hero-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--dark-red-element) 0%, rgba(255, 255, 255, 0.9) 100%);
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero h1 {
  font-size: 3.5rem;
  color: var(--white);
  margin-bottom: 2rem;
}

.hero h2 {
  font-size: 1.5rem;
  color: var(--white);
  margin-bottom: 1rem;
}

.hero .description {
  max-width: 800px;
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0 auto;
  color: var(--white);
  white-space: pre-line;
}

.product-nav {
  position: sticky;
  top: 80px;
  background: var(--white);
  padding: 1rem;
  display: flex;
  justify-content: center;
  gap: 2rem;
  border-bottom: 1px solid var(--border-color);
  z-index: 100;
}

.product-nav a {
  color: var(--primary-text-color);
  text-decoration: none;
  padding: 0.5rem 1rem;
}

.product-nav a:hover {
  color: var(--primary-color);
}

.product-section {
  padding: 4rem 1rem;
  max-width: 1700px;
  margin: 0 auto;
  scroll-margin-top: 100px;
}

.product-section:nth-child(odd) {
  background: var(--light-background);
}

.product-section h2 {
  font-size: 2.5rem;
  color: var(--primary-text-color);
  margin-bottom: 2rem;
  text-align: center;
}

.product-section p {
  font-size: 1.5rem;
  color: var(--secondary-text-color);
  margin-bottom: 2rem;
  line-height: 1.2;
}

.product-section h3 {
  font-size: 1.8rem;
  color: var(--secondary-text-color);
  margin-bottom: 2rem;
  line-height: 1.3;
}

.lead-text {
  font-size: 1.5rem;
  color: var(--secondary-text-color);
  margin-bottom: 2rem;
  line-height: 1.3;
  white-space: pre-line;
}

.features {
  margin: 3rem 0;
}

.features ul {
  list-style: none;
  padding: 0;
}

.features li {
  margin-bottom: 2rem;
}

.features li strong {
  display: block;
  color: var(--primary-text-color);
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.use-cases {
  padding: 2rem;
  border-radius: 8px;
  margin: 3rem 0;
}

/* Different gradients for each section */
#forecast .use-cases {
  background: linear-gradient(5deg, var(--logged-out-stand-out-green) 0%, var(--white) 100%);
}

#vagtplanlaegning .use-cases {
  background: linear-gradient(270deg, var(--logged-out-stand-out-green) 0%, var(--white) 100%);
}

#kundeanmeldelser .use-cases {
  background: linear-gradient(178deg, var(--logged-out-stand-out-green) 0%, var(--white) 100%);   
}   

#rapporter .use-cases {
  background: linear-gradient(350deg, var(--logged-out-stand-out-green) 0%, var(--white) 100%);
}

.cta-section {
  text-align: center;
  padding: 4rem 2rem;
  background: var(--dark-red-element);
  color: var(--white);
}

.cta-section h2 {
  margin-bottom: 1rem;
}

.cta-section p {
  margin-bottom: 2rem;
}

.feedback-section {
  margin: 4rem 0;
}

.feedback-cta {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

/* Add styles for the forecast section layout */
.product-content-forecast {
  display: flex;
  gap: 0rem;
  align-items: flex-start;
}

.product-description {
  flex: 0 0 30%;
  order: 1;
}

.product-image-container {
  flex: 0 0 70%;
  order: 2;
  max-width: 2200px;
  position: sticky;
  top: 1rem;
  align-self: flex-start;
  transform: translateX(15%);
  transition: transform 0.3s ease;
  height: fit-content;
  margin-top: -4rem;
}

.content-image {
  display: block;
  transition: transform 0.1s linear;
}


@media (max-width: 992px) {
  .product-content-forecast {
    flex-direction: column;
    align-items: center;
  }
  .product-description {
    flex: 1 1 auto;
    order: 1;
    width: 100%;
  }
  .product-image-container {
    flex: 1 1 auto;
    order: 2;
    position: relative;
    top: auto;
    align-self: auto;
    transform: translateX(0);
    max-width: 100%;
    margin: 2rem auto 0;
    height: auto;
  }
}

@media (max-width: 768px) {

  .product-nav {
    flex-wrap: wrap;
    gap: 1rem;
  }
  
  .product-section {
    padding: 2rem 1rem;
  }
  .hero {
    padding: 6rem 1rem 4rem 1.5rem;
  }
}

html {
  scroll-behavior: smooth;
}

.forecast-features-stepper {
  display: flex;
  align-items: flex-start;
  position: relative;
}

.forecast-features-steps {
  width: 50%;
}

#kundeanmeldelser .forecast-features-stepper {
  flex-direction: row-reverse;
}

#kundeanmeldelser .product-image-container {
  transform: translateX(0);
}

.forecast-feature-step {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 4rem;
}

.feature-text {
  white-space: pre-line;
}
</style> 